import { useState, useEffect, useCallback } from "react";
import { IService, IServiceFilters } from "src/domains";
import {
  queryGetServices,
  queryGetService,
  queryGetOwnServices,
} from "src/graphql";
import {
  OfferLikeToggleEvent,
  OfferUpdateEvent,
  offerUpdateEmit,
  CreateReviewEvent,
  TabChangeEvent,
  LogoutEvent,
} from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";
import { uploadToS3 } from "src/utils/s3";
import { useCognito, useAuth, AuthState } from "./cognito";
import {
  IMutationServiceCreateRequest,
  IMutationServiceUpdateRequest,
  IMutationServiceDeleteRequest,
  mutationServiceCreate,
  mutationServiceUpdate,
  mutationServiceDelete,
  mutationServiceView,
} from "src/graphql";
import { AppState, AppStateStatus } from "react-native";
import { useRecoilValue } from "recoil";

export const useOffer = (
  offerId?: string | undefined,
  incrementViews?: boolean
) => {
  const auth = useAuth();
  const [item, setItem] = useState<IService | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!offerId || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetService(
        { id: offerId },
        auth === AuthState.Auth ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY"
      );
      if (_isMount) {
        setItem(response.data.getService);
      }
      if (incrementViews && auth === AuthState.Auth) {
        await mutationServiceView(offerId);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [offerId, incrementViews, auth]);

  useEffect(() => {
    refresh();
  }, [offerId, refresh]);

  useEffect(() => {
    const offerLikeToggleListener = EventRegister.addEventListener(
      OfferLikeToggleEvent,
      () => refresh()
    );
    const offerUpdateListener = EventRegister.addEventListener(
      OfferUpdateEvent,
      () => refresh()
    );
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(offerLikeToggleListener as string);
      EventRegister.removeEventListener(offerUpdateListener as string);
      EventRegister.removeEventListener(createReviewListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [item, loading, error, refresh] as const;
};

export const useOwnOffers = () => {
  const [items, setItems] = useState<IService[]>([]);
  const [, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(null);
    }
    try {
      const response = await queryGetOwnServices({
        limit: 10,
        // offset: next
        //   ? this.state.recommendationTotalCount -
        //   this.state.recommendationResults.length
        //   : 0,
      });
      if (_isMount) {
        setItems(response.data.getOwnServices?.items || []);
      }
      if (_isMount) {
        setTotalCount(response.data.getOwnServices?.totalCount || null);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, []);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const offerUpdateListener = EventRegister.addEventListener(
      OfferUpdateEvent,
      () => refresh()
    );
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "Offers") {
          refresh();
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(createReviewListener as string);
      EventRegister.removeEventListener(offerUpdateListener as string);
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setItems([]);
      setTotalCount(0);
      setError(null);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [items, loading, error, refresh] as const;
};

export const useRelatedOffers = (filter?: IServiceFilters) => {
  const auth = useAuth();
  const [offers, setOffers] = useState<IService[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!filter || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetServices(
        {
          filter,
          limit: 10,
        },
        auth === AuthState.Auth ? "AMAZON_COGNITO_USER_POOLS" : "API_KEY"
      );
      if (_isMount) {
        setOffers(response?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [filter, auth]);

  useEffect(() => {
    refresh();
  }, [filter, auth, refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [offers, loading, error] as const;
};

export const useOfferActions = () => {
  const [cognitoUsername] = useCognito();
  const createOffer = async (params: IMutationServiceCreateRequest) => {
    await mutationServiceCreate(params);
    offerUpdateEmit(params.input.id, "create");
  };
  const updateOffer = async (params: IMutationServiceUpdateRequest) => {
    await mutationServiceUpdate(params);
    offerUpdateEmit(params.input.id, "update");
  };
  const deleteOffer = async (params: IMutationServiceDeleteRequest) => {
    await mutationServiceDelete(params);
    offerUpdateEmit(params.id, "delete");
  };
  const uploadOfferImage = async (url: string, offerId: string) => {
    if (!cognitoUsername) {
      throw Error("Cognito user is empty");
    }
    return await uploadToS3(url, `user-${cognitoUsername}/offer-${offerId}/`);
  };

  return { createOffer, updateOffer, deleteOffer, uploadOfferImage } as const;
};
